import type { OPCS4Code } from "@/clinical-codes/types/ClinicalCodeTypes";
import type { Icd10Code } from "@/clinical-codes/useSearchIcd10Codes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounce } from "@/hooks/use-debounce";
import { supabase } from "@/integrations/supabase/client";
import type { QueryData } from "@supabase/supabase-js";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Settings } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react"; // Added missing import
import { useParams } from "react-router";
import { toast } from "sonner";
import { DiagnosesSection, type CodeAccessorConfig } from "./DiagnosesSection";
import { NewMedicalTextDialog } from "./NewMedicalTextDialog";
import { getPatientEncounterLabel } from "./patientEncounterLabel";
import { ProceduresSection } from "./ProceduresSection";

const getTestEncounterQuery = () =>
  supabase.from("patient_encounters").select(
    `*,
            patient:patients(*),
            patient_encounter_medical_text_examples(
              *,
              medical_text_examples(*)
            ),
            encounter_expected_diagnoses:test_encounter_expected_diagnoses(
              *,
              icd10_code:icd10_codes(
                id, 
                code,
                description
              )
            ),
            encounter_expected_procedures:test_encounter_expected_procedures(
              *,
              opcs4_code:opcs4_codes(id, code, description)
            )
         `,
  );

type TestEncounter = QueryData<ReturnType<typeof getTestEncounterQuery>>[number];

type ExpectedDiagnosis = TestEncounter["encounter_expected_diagnoses"][number];

// Custom code accessor for ExpectedDiagnosis structure
const expectedDiagnosisCodeAccessor: CodeAccessorConfig<ExpectedDiagnosis> = {
  getCode: (item) => item.icd10_code.code,
  getDescription: (item) => item.icd10_code.description,
  getCodeId: (item) => item.icd10_code.id,
  createFromCode: (code: Icd10Code, existingItems: ExpectedDiagnosis[]) => ({
    id: crypto.randomUUID(),
    icd10_code_id: code.id,
    is_optional: false,
    order_index: existingItems.length,
    patient_encounter_id: "to_be_replaced",
    icd10_code: {
      id: code.id,
      code: code.code,
      description: code.description,
    },
  }),
  updateOrderIndex: (items) => items.map((item, index) => ({ ...item, order_index: index })),
};

function PatientEncounterDetails({ encounterId }: { encounterId: string }) {
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<ExpectedDiagnosis[] | null>(null);
  const [selectedProcedures, setSelectedProcedures] = useState<OPCS4Code[] | null>(null);
  const [activeTab, setActiveTab] = useState("0"); // Track active tab

  const [medicalTextDialogId, setMedicalTextDialogId] = useState<string | null>(null);

  // Debounce the selected codes to trigger auto-save
  const debouncedDiagnoses = useDebounce(selectedDiagnoses, 1000); // 1 second delay
  const debouncedProcedures = useDebounce(selectedProcedures, 1000); // 1 second delay

  const toggleOptionalStatus = (id: string) => {
    setSelectedDiagnoses((prev) => (prev ?? []).map((d) => (d.id === id ? { ...d, is_optional: !d.is_optional } : d)));
  };

  const handleProceduresChange = useCallback((procedures: OPCS4Code[]) => {
    setSelectedProcedures(procedures);
  }, []);

  // Fetch encounter and related medical_text_examples
  const {
    data: encounter,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["patient-encounter-details", encounterId],
    queryFn: async () => {
      const { data, error } = await getTestEncounterQuery().eq("id", encounterId).single();
      if (error) throw error;
      return data;
    },
    enabled: !!encounterId,
  });

  // Initialize selected codes from encounter data
  React.useEffect(() => {
    if (encounter) {
      setSelectedDiagnoses(
        Array.isArray(encounter.encounter_expected_diagnoses) ? encounter.encounter_expected_diagnoses : [],
      );
      setSelectedProcedures(
        Array.isArray(encounter.encounter_expected_procedures)
          ? encounter.encounter_expected_procedures.map((p) => ({
              id: p.opcs4_code.id,
              code: p.opcs4_code.code,
              description: p.opcs4_code.description,
            }))
          : [],
      );
    }
  }, [encounter]);

  // const replaceExpectedCodes = useCallback(async () => {
  //   await Promise.all([

  //   ]);
  // }, [encounterId, debouncedDiagnoses, debouncedProcedures]);

  const prevDiagnoses = useRef(debouncedDiagnoses);
  const prevProcedures = useRef(debouncedProcedures);

  const mutationReplaceProcedures = useMutation({
    mutationFn: async (_procedures: OPCS4Code[]) => {
      await supabase.rpc("replace_test_encounter_expected_procedures", {
        p_patient_encounter_id: encounterId,
        p_new_rows: (debouncedProcedures ?? []).map((p, index) => ({
          opcs4_code_id: p.id,
          order_index: index,
        })),
      });
    },
    onSuccess: () => {
      toast.success("Procedures saved");
    },
    onError: (error) => {
      console.error("Failed to save procedure codes", error);
      toast.error("Failed to save procedure codes");
    },
  });

  const mutationReplaceDiagnosis = useMutation({
    mutationFn: async (diagnoses: ExpectedDiagnosis[]) => {
      await supabase
        .rpc("replace_test_encounter_expected_diagnoses", {
          p_patient_encounter_id: encounterId,
          p_new_rows: diagnoses.map((d) => ({
            icd10_code_id: d.icd10_code_id,
            order_index: d.order_index,
            is_optional: d.is_optional,
          })),
        })
        .throwOnError();
    },
    onSuccess: () => {
      toast.success("Diagnoses saved");
    },
    onError: (error) => {
      console.error("Failed to save diagnosis codes", error);
      toast.error("Failed to save diagnosis codes");
    },
  });

  useEffect(() => {
    if (!prevProcedures.current) {
      prevProcedures.current = debouncedProcedures;
      return;
    }
    mutationReplaceProcedures.mutate(debouncedProcedures ?? []);
  }, [debouncedProcedures, mutationReplaceProcedures.mutate]);

  useEffect(() => {
    if (!prevDiagnoses.current) {
      prevDiagnoses.current = debouncedDiagnoses;
      return;
    }

    mutationReplaceDiagnosis.mutate(debouncedDiagnoses ?? []);
  }, [debouncedDiagnoses, mutationReplaceDiagnosis.mutate]);

  if (isLoading) {
    return <div className="p-8 text-center">Loading...</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-red-500">Failed to load encounter.</div>;
  }
  if (!encounter) {
    return <div className="p-8 text-center text-muted-foreground">Encounter not found.</div>;
  }

  return (
    <div className="flex gap-8 min-h-screen">
      <NewMedicalTextDialog
        open={!!medicalTextDialogId}
        onClose={() => setMedicalTextDialogId(null)}
        medicalTextId={medicalTextDialogId ?? undefined}
      />
      {/* Ensure full viewport height */}
      {/* Left column: medical_text_examples in tabs */}
      <div className="w-1/2 border-r h-full overflow-y-auto p-2">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3">
            {encounter.patient_encounter_medical_text_examples.map((item, index) => (
              <TabsTrigger key={item.medical_text_examples.id} value={index.toString()} className="text-xs px-2 py-1">
                {item.medical_text_examples.test_type || "Unknown"}
              </TabsTrigger>
            ))}
          </TabsList>

          {encounter.patient_encounter_medical_text_examples.map((item, index) => (
            <TabsContent key={item.medical_text_examples.id} value={index.toString()} className="mt-4">
              <Card className="rounded-md border bg-muted/30">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                      {item.medical_text_examples.test_type || "Unknown"}
                    </span>
                    {item.medical_text_examples.label ? (
                      <span className="text-muted-foreground text-sm">({item.medical_text_examples.label})</span>
                    ) : null}
                    <span className="text-muted-foreground text-sm">
                      ({item.medical_text_examples.sequence_number})
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={() => setMedicalTextDialogId(item.medical_text_example_id)}
                    title="View/Edit Medical Text Example"
                    aria-label="View/Edit Medical Text Example"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <pre className="text-sm text-muted-foreground font-sans whitespace-pre-wrap break-words bg-background rounded p-2 overflow-x-auto max-w-full">
                    {item.medical_text_examples.text}
                  </pre>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
      {/* Right column: expected codes */}
      <div className="flex-1 h-full overflow-auto p-6 space-y-6 relative min-h-screen">
        <h2 className="text-2xl font-bold mb-4">
          Final expected Diagnoses & Procedures for encounter '{getPatientEncounterLabel(encounter)}'
        </h2>
        <ProceduresSection
          selectedProcedures={selectedProcedures || []}
          onProceduresChange={handleProceduresChange}
          onFormChange={() => {}}
        />
        <DiagnosesSection
          selectedDiagnoses={selectedDiagnoses || []}
          onDiagnosesChange={setSelectedDiagnoses}
          onToggleOptional={toggleOptionalStatus}
          onFormChange={() => {}}
          codeAccessor={expectedDiagnosisCodeAccessor}
          label="Patient Encounter Diagnoses"
        />
      </div>
    </div>
  );
}

export { PatientEncounterDetails };

export default function PatientEncounterDetailsWrapper() {
  const { "encounter-id": encounterId } = useParams();

  if (!encounterId) return null;
  return <PatientEncounterDetails encounterId={encounterId} />;
}
